import { data } from './../../../components/layouts/SideNav/data/index';
import { User, Wallet } from './types';
import { cache, useState } from 'react';
import useBackend from '@/backend';
import { useAccessToken, useLogout, useUserStoreActions } from '@/store';
import { redirect } from 'next/navigation';
import { getAffiliateToken, removeAffiliateToken } from '@/utils/myAffiliateHelpers';

/* --------------------------------- OPTIONS -------------------------------- */
const headers = {
	'Content-Type': 'application/json',
	Authorization: null,
};

const route = '/user/get-user';

interface Args {
	isInitialUserCreation?: boolean;
}
/* -------------------------------------------------------------------------- */

export const useGetUser = () => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const { url, getAccessToken } = useBackend();
	const { setUser, setUserWallets, setActiveWallet } = useUserStoreActions();
	const logout = useLogout();

	const getUser = cache(async ({ isInitialUserCreation }: Args) => {
		// await new Promise((resolve) => setTimeout(resolve, 4000));
		try {
			setLoading(true);
			setError(null);
			const apiUrl = `${url}${route}`;

			/* -------------------------------------------------------------------------- */
			const token = await getAccessToken({});

			if (!token) throw new Error('Failed to load');

			headers.Authorization = token;

			/* -------------------------------------------------------------------------- */

			if (isInitialUserCreation) {
				const affiliateToken = getAffiliateToken();
				if (affiliateToken) headers['X-Affiliate-Token'] = affiliateToken;
				try {
					const response = await fetch(apiUrl, {
						headers,
					});
					if (affiliateToken) removeAffiliateToken();
				} catch {}
			}
			const response = await fetch(apiUrl, {
				headers,
			});

			const userData = await response.json();

			if (userData?.errors) {
				const error = userData?.errors[0];
				if (error?.name === 'AuthenticationErrorType') {
					logout();
					redirect('/');
					throw new Error('Failed to load user data');
				}
			}

			if (!response.ok) {
				throw new Error('Failed to load user data');
			}
			const user = userData?.data?.user;
			if (!user) {
				throw new Error('Failed to load user data');
			}

			/* -------------------------------------------------------------------------- */
			setUser(user);

			if (user.wallets.length > 0) {
				setUserWallets(user.wallets);
				const defaultWallet =
					user.wallets.find((wallet: Wallet) => wallet.isDefault) ||
					user.wallets[0];

				setActiveWallet(defaultWallet);
			}
			return;

			/* -------------------------------------------------------------------------- */
		} catch (e: unknown) {
			const message = e instanceof Error ? e.message : 'Unknown error';

			setError(message);
			if (e instanceof Error) return { user: null, error: e.message };

			return { user: null, error: 'Failed to load user data' };
		} finally {
			setLoading(false);
		}
	});
	return {
		loading,
		error,
		getUser,
	};
};
